import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  watch,
  type ComputedRef,
} from 'vue'
import { PAGE_FORM_GROUP_KEY, usePipe } from './usePipe'

/**
 * Register a form input with its nearest form group.
 *
 * Inputs that participate in submit-time validation call this in their
 * `<script setup>`, providing a `validate` callback that returns whether
 * the current value is valid. On submit (CTA click, soft-keyboard Enter,
 * or the `submit_form` event processor), the form group runs every
 * registered input's `validate`; if any returns false, the rest of the
 * onClick chain is skipped.
 *
 * Group key is **injected** (not composed from this input's own ancestry),
 * so an input rendered inside a SharedNodeRef whose template lives inside a
 * page-level Form correctly belongs to that outer Form. The `<Form>`
 * component or `RenderComponents`'s page-implicit fallback provides it.
 *
 * The instance key for the input is composed `[...sharedNodeRefAncestry, nodeId]`
 * (mirrors `useComponentProcessors`). `data-id` is read from $attrs — set
 * by `RenderComponents` on every rendered component.
 *
 * Usage:
 * ```ts
 * const { showErrors, requestSubmit, markTouched } = useFormInput({
 *   validate: () => isValid(value.value),
 *   disableRealtimeValidation: () => fields.value.disableRealtimeValidation === true,
 * })
 * ```
 *
 * `showErrors`: bind to error UI (e.g. red border) — true once the input
 * has been touched OR after the form has attempted submit.
 * `markTouched()`: call from a blur handler when realtime validation is
 * enabled (the default). The form scope also calls it on every input at
 * submit time so errors surface even for never-touched fields.
 * `requestSubmit()`: bind to Enter/Done — routes through the form's
 * registered submit target (the CTA) when present, otherwise just runs
 * validation directly.
 */
export interface UseFormInputOptions {
  validate: () => boolean | Promise<boolean>
  // Reactive accessor — when true, errors only show after submit, never on blur.
  disableRealtimeValidation?: () => boolean
  // Bare variable keys (no `var.` prefix) this input writes transiently
  // (`setData(..., { persist: false })`). On a valid submit of this input's
  // form group, exactly these keys are promoted to the persisted store. Inputs
  // that don't defer persistence (selection components, validation-only users)
  // omit this.
  persistKeys?: () => (string | null | undefined)[]
}

export interface UseFormInputReturn {
  showErrors: ComputedRef<boolean>
  markTouched: () => void
  // Resets the touched flag so errors stop showing until the next blur /
  // submit attempt. Useful on focus when `disableRealtimeValidation` is on —
  // post-submit errors clear as the user starts fixing the field.
  clearTouched: () => void
  // Call from a blur handler. No-ops when `disableRealtimeValidation()`
  // returns true, so the input stays clean until the user submits.
  handleBlur: () => void
  // Returns true iff a submit target (e.g. a CTA in scope) was invoked.
  // Callers can use this to decide whether to fall back to legacy behavior
  // (e.g. TextInput emitting its own `onSubmit` event when no CTA is wired).
  requestSubmit: () => Promise<{ targetInvoked: boolean }>
  hasFormGroup: ComputedRef<boolean>
}

export function useFormInput(opts: UseFormInputOptions): UseFormInputReturn {
  const pipe = usePipe()
  const attrs = useAttrs()
  const ancestry = inject<ComputedRef<readonly string[]>>(
    'sharedNodeRefAncestry',
    computed(() => []),
  )
  const injectedGroup = inject<ComputedRef<string | null> | null>('formGroupKey', null)

  const groupKey = computed<string>(() => injectedGroup?.value ?? PAGE_FORM_GROUP_KEY)

  const inputKey = computed<string | null>(() => {
    const nodeId = attrs['data-id'] as string | undefined
    if (!nodeId) return null
    return [...ancestry.value, nodeId].join('/')
  })

  const touched = ref(false)
  const markTouched = () => {
    touched.value = true
  }
  const clearTouched = () => {
    touched.value = false
  }

  const showErrors = computed(() => touched.value)

  // Track the actually-registered keys so unregister always targets the right
  // bucket, even if the computed has since recomputed (SNR reuse during nav).
  let lastInputKey: string | null = null
  let lastGroup: string | null = null

  const register = (group: string, key: string) => {
    pipe.registerFormInput(group, {
      inputKey: key,
      validate: opts.validate,
      markTouched,
      persistKeys: opts.persistKeys,
    })
    lastInputKey = key
    lastGroup = group
  }
  const unregister = () => {
    if (lastGroup && lastInputKey) {
      pipe.unregisterFormInput(lastGroup, lastInputKey)
    }
    lastInputKey = null
    lastGroup = null
  }

  onMounted(() => {
    const key = inputKey.value
    if (!key) {
      console.warn(
        '[useFormInput] data-id attr not found on component — cannot register. ' +
          'Is this component rendered via RenderComponents?',
      )
      return
    }
    register(groupKey.value, key)
  })

  watch([inputKey, groupKey], ([newKey, newGroup]) => {
    if (newKey === lastInputKey && newGroup === lastGroup) return
    unregister()
    if (newKey) register(newGroup, newKey)
  })

  onBeforeUnmount(unregister)

  const requestSubmit = async () => {
    // Microtask yield lets a sibling CTA's onMounted complete registration
    // when the user pressed Enter immediately after the form mounted.
    await Promise.resolve()
    const target = pipe.getFormSubmitTarget(groupKey.value)
    if (target) {
      await target.submit()
      return { targetInvoked: true }
    }
    // No submit target — still surface errors and run validation so the
    // user gets feedback even on an authored-incomplete form.
    await pipe.submitFormGroup(groupKey.value)
    return { targetInvoked: false }
  }

  const handleBlur = () => {
    if (opts.disableRealtimeValidation?.()) return
    markTouched()
  }

  return {
    showErrors,
    markTouched,
    clearTouched,
    handleBlur,
    requestSubmit,
    hasFormGroup: computed(() => injectedGroup?.value != null),
  }
}
